import React from "react";
import { Box, Container, Link, Paper, Typography } from "@material-ui/core";
import {
  isInternationalOnlyDomain,
  useDomainFeatures,
} from "../../context/DomainFeaturesContext";

const pageWrap = { paddingTop: 28, paddingBottom: 48 };
const card = {
  padding: "32px 36px 40px",
  borderRadius: 16,
  border: "1px solid #e8eef4",
  boxShadow: "0 8px 28px rgba(15, 43, 74, 0.06)",
};
const titleStyle = { fontWeight: 800, color: "#0F3B62", letterSpacing: "-0.02em" };
const metaStyle = { color: "#64748b", marginTop: 8, lineHeight: 1.6 };
const introStyle = {
  lineHeight: 1.8,
  color: "#334155",
  marginTop: 16,
  background: "#f7fafc",
  border: "1px solid #e8eef4",
  borderRadius: 12,
  padding: "14px 16px",
};
const headingStyle = {
  fontWeight: 700,
  color: "#0F3B62",
  marginTop: 28,
  fontSize: "1.05rem",
};
const subHeadingStyle = {
  fontWeight: 700,
  color: "#1f4a73",
  marginTop: 16,
  fontSize: "0.95rem",
};
const bodyStyle = { lineHeight: 1.8, color: "#334155", marginTop: 8 };
const listStyle = { margin: "8px 0 0", paddingLeft: 22, color: "#334155", lineHeight: 1.8 };

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="h6" style={headingStyle}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function Privacy() {
  const { brandDomain, wwwDomain, supportEmail, domainName } = useDomainFeatures();
  const isUs = isInternationalOnlyDomain(domainName);

  const copy = isUs
    ? {
        lastUpdated: "Last updated on November 25, 2024",
        intro: `Srshta Tech Solutions Pvt Ltd is committed to protecting the privacy of users who interact with our Montessori School Activity Monitor Software ("${wwwDomain}"). This Privacy Policy outlines our practices regarding the collection, use, disclosure, and protection of personal information collected through the Software. By using the Software, you agree to the practices described in this Privacy Policy.`,
        legalLead: "We process personal information based on the following legal grounds:",
        legalItems: [
          { label: "Consent:", text: " We may rely on consent from parents/guardians or staff for specific uses of personal data." },
          { label: "Legitimate Interests:", text: " Data may be processed for legitimate educational and business interests, including improving learning outcomes." },
          { label: "Contractual Obligations:", text: " To fulfill any contractual obligations with the School regarding the Software’s use." },
          { label: "Legal Compliance:", text: " To comply with applicable U.S. laws, regulations, or legal processes, including COPPA where applicable." },
        ],
        retention:
          "We retain personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy or as required by law. Upon request from the school, parents, or guardians, data may be deleted or anonymized, subject to any legal obligations to retain it.",
        children:
          "For data involving children under 13, we require parental or guardian consent before collecting, processing, or sharing any personal data, consistent with the Children’s Online Privacy Protection Act (COPPA). Schools are responsible for obtaining necessary consents and providing proof if requested.",
        cookies:
          "Users can control cookie settings through their browser preferences. Disabling cookies may affect certain features of the Software.",
        rightsLead: "Depending on your location, you may have rights related to your personal data, including:",
        transfers:
          "Personal data may be transferred to and processed in the United States and other countries where data protection laws may differ. We take steps to ensure that data transfers are protected by appropriate safeguards, such as standard contractual clauses or other lawful mechanisms.",
        contactLead:
          "For any questions or concerns about this Privacy Policy or how we handle personal information, please contact us at:",
      }
    : {
        lastUpdated: "Last updated on 25th Nov, 2024",
        intro: `Srshta Tech Solutions Pvt Ltd, we are committed to protecting the privacy of users who interact with our Montessori School Activity Monitor Software ("${wwwDomain}"). This Privacy Policy outlines our practices regarding the collection, use, disclosure, and protection of personal information collected through the Software. By using the Software, you agree to the practices described in this Privacy Policy.`,
        legalLead: "We process personal information based on the following legal grounds:",
        legalItems: [
          { label: "Consent:", text: " We may rely on consent provided by parents/guardians or staff for specific uses of personal data." },
          { label: "Legitimate Interests:", text: " Data may be processed for legitimate educational interests, including enhancing learning outcomes." },
          { label: "Contractual Obligations:", text: " To fulfil any contractual obligations with the School regarding the Software’s use." },
          { label: "Legal Compliance:", text: " To comply with applicable laws, regulations, or legal processes." },
        ],
        retention:
          "We retain personal data only for as long as necessary to fulfil the purposes outlined in this Privacy Policy or as required by law. Upon request from the school, parents, or guardians, data may be deleted or anonymised, subject to any legal obligations to retain it.",
        children:
          "For data involving children under 13 (or the relevant age of consent), we require parental or guardian consent before collecting, processing, or sharing any personal data. Schools are responsible for obtaining necessary consents and providing proof if requested.",
        cookies:
          "Users can control cookie settings through their browser preferences. Disabling cookies may affect certain functionalities of the Software.",
        rightsLead: "Depending on your location, you may have rights related to your personal data, including:",
        transfers:
          "Personal data may be transferred to and processed in countries outside the country of origin where data protection laws may differ. We take steps to ensure that data transfers are protected by appropriate safeguards, such as standard contractual clauses or other lawful mechanisms.",
        contactLead:
          "For any questions or concerns about this Privacy Policy or how we handle personal information, please contact us at:",
      };

  return (
    <Container maxWidth="md" style={pageWrap}>
      <Paper elevation={0} style={card}>
        <Typography variant="h4" component="h1" style={titleStyle}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" style={metaStyle}>
          Montessori School Activity Monitor Software, “{brandDomain}” · {copy.lastUpdated}
        </Typography>
        <Typography variant="body2" style={introStyle}>
          {isUs
            ? `This Privacy Policy explains how we collect, use, and protect personal information on ${wwwDomain}.`
            : `This Privacy Policy explains how we collect, use and protect personal information on ${wwwDomain}.`}
        </Typography>

        <Section title="1. Introduction">
          <Typography variant="body1" style={bodyStyle}>
            {copy.intro}
          </Typography>
        </Section>

        <Section title="2. Information We Collect">
          <Typography variant="body1" style={bodyStyle}>
            We may collect the following types of information when the Software is used:
          </Typography>
          <Typography variant="subtitle1" style={subHeadingStyle}>
            a. Personal Information
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>
              <strong>Student Information:</strong> Name, age, attendance records, activity progress, assessment results, and any other educational data entered by the school.
            </li>
            <li>
              <strong>Staff Information:</strong> Names, email addresses, usernames, roles within the school, and other relevant information provided by the School.
            </li>
            <li>
              <strong>Parent/Guardian Information (if applicable):</strong> Contact details such as email addresses for communication purposes.
            </li>
          </Box>
          <Typography variant="subtitle1" style={subHeadingStyle}>
            b. Usage Data
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>
              Data about how the Software is accessed and used, including activity logs, session durations, user interactions, and device information (e.g., IP address, browser type, operating system).
            </li>
          </Box>
        </Section>

        <Section title="3. How We Use the Information">
          <Typography variant="body1" style={bodyStyle}>
            We use the collected information to:
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>Facilitate the educational processes within the Montessori environment.</li>
            <li>Provide, maintain, and improve the Software’s functionality.</li>
            <li>Track student progress and create activity reports.</li>
            <li>Communicate with authorized staff members regarding updates, support, or other relevant information.</li>
            <li>Ensure the security and integrity of the Software.</li>
            <li>Comply with legal obligations or enforce applicable terms and conditions.</li>
          </Box>
        </Section>

        <Section title="4. Legal Basis for Data Processing">
          <Typography variant="body1" style={bodyStyle}>
            {copy.legalLead}
          </Typography>
          <Box component="ul" style={listStyle}>
            {copy.legalItems.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong>
                {item.text}
              </li>
            ))}
          </Box>
        </Section>

        <Section title="5. Data Sharing and Disclosure">
          <Typography variant="body1" style={bodyStyle}>
            We do not sell or rent personal data to third parties. However, we may share information in the following situations:
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>
              <strong>With the School:</strong> Authorized school personnel have access to data to facilitate education and administration.
            </li>
            <li>
              <strong>Service Providers:</strong> Third-party vendors who provide essential services to the Software (e.g., hosting, data analytics, technical support), subject to strict data protection agreements.
            </li>
            <li>
              <strong>Legal Requirements:</strong> If required by law or in response to valid legal processes, including subpoenas or court orders.
            </li>
            <li>
              <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, personal data may be transferred to a successor entity.
            </li>
          </Box>
        </Section>

        <Section title="6. Data Security">
          <Typography variant="body1" style={bodyStyle}>
            We implement industry-standard security measures to protect personal data from unauthorized access, loss, misuse, or alteration. These measures include:
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>
              <strong>Data Encryption:</strong> Personal data is encrypted during transmission and at rest to ensure confidentiality.
            </li>
            <li>
              <strong>Access Controls:</strong> Access to the Software is restricted to authorized users through unique credentials.
            </li>
            <li>
              <strong>Regular Security Audits:</strong> Periodic security reviews and vulnerability assessments are conducted to ensure data protection.
            </li>
          </Box>
        </Section>

        <Section title="7. Data Retention">
          <Typography variant="body1" style={bodyStyle}>
            {copy.retention}
          </Typography>
        </Section>

        <Section title="8. Parental/Guardian Consent">
          <Typography variant="body1" style={bodyStyle}>
            {copy.children}
          </Typography>
        </Section>

        <Section title="9. Cookies and Tracking Technologies">
          <Typography variant="body1" style={bodyStyle}>
            The Software may use cookies or similar technologies to:
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>Improve user experience by remembering preferences and settings.</li>
            <li>
              {isUs
                ? "Analyze usage patterns for optimization purposes."
                : "Analyse usage patterns for optimisation purposes."}
            </li>
            <li>Ensure security during user sessions.</li>
          </Box>
          <Typography variant="body1" style={bodyStyle}>
            {copy.cookies}
          </Typography>
        </Section>

        <Section title="10. Your Data Protection Rights">
          <Typography variant="body1" style={bodyStyle}>
            {copy.rightsLead}
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>
              <strong>Access:</strong> The right to request copies of personal data.
            </li>
            <li>
              <strong>Correction:</strong> The right to request corrections to inaccurate or incomplete data.
            </li>
            <li>
              <strong>Deletion:</strong> The right to request deletion of personal data.
            </li>
            <li>
              <strong>Restriction:</strong> The right to request restriction on processing personal data.
            </li>
            <li>
              <strong>Portability:</strong> The right to request a copy of your data in a commonly used format.
            </li>
            <li>
              <strong>Objection:</strong> The right to object to data processing for certain purposes.
            </li>
        </Box>
          <Typography variant="body1" style={bodyStyle}>
            To exercise any of these rights, please contact us at{" "}
            <Link href={`mailto:${supportEmail}`} color="primary">
              {supportEmail}
            </Link>
            .
          </Typography>
        </Section>

        <Section title="11. Data Transfers">
          <Typography variant="body1" style={bodyStyle}>
            {copy.transfers}
          </Typography>
        </Section>

        <Section title="12. Changes to this Privacy Policy">
          <Typography variant="body1" style={bodyStyle}>
            We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal obligations. Any changes will be posted within the Software, and significant updates will be communicated to the school via email.
          </Typography>
        </Section>

        <Section title="13. Contact Us">
          <Typography variant="body1" style={bodyStyle}>
            {copy.contactLead}
          </Typography>
          <Typography variant="body1" style={{ ...bodyStyle, fontWeight: 600, color: "#0F3B62" }}>
            Email:{" "}
            <Link href={`mailto:${supportEmail}`} color="inherit" underline="always">
              {supportEmail}
            </Link>
          </Typography>
        </Section>
      </Paper>
    </Container>
  );
}
